import { useGetAccountStatesQueryOptions } from "@/api/account-state/account-state"
import { useGetAccountQueryOptions } from "@/api/account/account"
import { useGetChangeOfTenancyMoveWithUsQueryOptions } from "@/api/change-of-tenancy/change-of-tenancy"
import { useGetCustomerQueryOptions } from "@/api/customer/customer"
import { SupplyState, type MoveWithUsChangeOfTenancy } from "@/api/model"
import { ExternalRoute } from "@/constants/externalRoute"
import { MyAccountRoute } from "@/constants/routes/myAccount"
import { removeLeadingSlash } from "@/lib/utils"
import { getPublicWebsiteUrl } from "@/lib/websiteUrls"
import { isFeatureFlagOn } from "@/plugins/router/middleware/isFeatureFlagOn"
import ErrorReporter from "@/services/ErrorReporter"
import { LinkOpenerService } from "@/services/shared/LinkOpenerService"
import TrackingService from "@/services/TrackingService"
import { useQueryClient } from "@tanstack/vue-query"
import { NavigationGuard } from "vue-router"

import { isMoveWithUsNotFound } from "@/views/myaccount/moving-home/moveWithUsNotFound"
import { isMoveWithUsInFlight } from "@/views/myaccount/moving-home/movingHomeState"

export const moveWithUsGuard: NavigationGuard = async (to, from) => {
  const queryClient = useQueryClient()

  if (!(await isFeatureFlagOn(queryClient, "cotStayUpfront"))) return

  if (to.path.startsWith(MyAccountRoute.moveWithUs)) {
    TrackingService.forceSessionReplayRecording()
  }

  try {
    const { accountIDLastVisited: accountId } =
      await queryClient.ensureQueryData(useGetCustomerQueryOptions())
    if (!accountId) throw new Error("accountId not found")

    const [{ supply }, account] = await Promise.all([
      queryClient.ensureQueryData(useGetAccountStatesQueryOptions(accountId)),
      queryClient.ensureQueryData(useGetAccountQueryOptions(accountId)),
    ])

    if (account.isClosed) return { path: MyAccountRoute.home }
    const hasMoveOut =
      supply === SupplyState.futureMoveOut || supply === SupplyState.movedOut

    let record: MoveWithUsChangeOfTenancy | undefined
    try {
      // ensureQueryData would return the invalidated record from cache.
      record = await queryClient.fetchQuery(
        useGetChangeOfTenancyMoveWithUsQueryOptions(accountId),
      )
    } catch (error) {
      if (!isMoveWithUsNotFound(error)) throw error
    }
    if (record) {
      // After the move-in date only Moving Home lets the customer back in: a
      // second journey would book a second move-out.
      if (
        to.path !== MyAccountRoute.movingHome ||
        isMoveWithUsInFlight(record)
      ) {
        return {
          path: MyAccountRoute.moveWithUsStatus,
          replace: from.path.startsWith(MyAccountRoute.moveWithUs),
        }
      }
      return
    }

    // After the record check, so a Move With Us keeps its own page
    if (hasMoveOut) {
      await LinkOpenerService.redirect(
        `${getPublicWebsiteUrl()}${removeLeadingSlash(ExternalRoute.moveOutStatus)}`,
      )
      return false
    }
  } catch (error) {
    ErrorReporter.report(error)
    if (to.path === MyAccountRoute.movingHome) return
    return { path: MyAccountRoute.movingHome }
  }
}
